import AppLayout from "@/components/app-layout";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Award, Users, ArrowRight, MessageSquare, Loader2, Star, Handshake } from "lucide-react";
import { useState, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { format } from "date-fns";
import { usePageTitle } from "@/hooks/use-page-title";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";

interface PublicRealtor {
  id: number;
  contactName: string;
  companyName: string;
  logoUrl?: string | null;
  details: Record<string, unknown>;
  createdAt: string;
}

const AVATAR_COLORS = ["bg-blue-500", "bg-violet-500", "bg-green-500", "bg-amber-500", "bg-pink-500"];

function realtorInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);
}

function RealtorAvatar({ realtor, size = "md" }: { realtor: PublicRealtor; size?: "sm" | "md" }) {
  const color = AVATAR_COLORS[realtor.id % AVATAR_COLORS.length];
  const initials = realtorInitials(realtor.contactName);
  const sizeClass = size === "sm" ? "w-8 h-8 text-xs" : "w-10 h-10 text-sm";

  if (realtor.logoUrl) {
    return (
      <img
        src={realtor.logoUrl}
        alt={realtor.contactName}
        className={`${sizeClass} rounded-full object-cover shrink-0`}
        data-testid={`img-realtor-avatar-${realtor.id}`}
      />
    );
  }
  return (
    <div
      className={`${sizeClass} rounded-full ${color} flex items-center justify-center text-white font-bold shrink-0`}
      data-testid={`avatar-realtor-initials-${realtor.id}`}
    >
      {initials}
    </div>
  );
}

function ConnectDialog({ realtor, open, onClose }: { realtor: PublicRealtor; open: boolean; onClose: () => void }) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [message, setMessage] = useState("");

  const connectMutation = useMutation({
    mutationFn: async (msg: string) => {
      const res = await apiRequest("POST", `/api/app/realtors/${realtor.id}/connect`, { message: msg });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Your message has been sent", description: "Expect a response within two days." });
      onClose();
      setMessage("");
      navigate("/app/connections");
    },
    onError: () => toast({ title: "Failed to send request", variant: "destructive" }),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => { if (!o && connectMutation.isPending) return; if (!o) onClose(); }}
    >
      <DialogContent
        onEscapeKeyDown={(e) => { if (connectMutation.isPending) e.preventDefault(); }}
        onInteractOutside={(e) => { if (connectMutation.isPending) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (connectMutation.isPending) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>Work with {realtor.contactName}</DialogTitle>
          <DialogDescription>
            Send a message to connect. The realtor will receive a notification and reach out to you.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor={`msg-${realtor.id}`}>Your message</Label>
            <Textarea
              id={`msg-${realtor.id}`}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Hi, I'm looking to buy a co-living property in..."
              data-testid="input-connect-message"
            />
          </div>
          <Button
            className="w-full"
            onClick={() => { if (message.trim()) connectMutation.mutate(message.trim()); }}
            disabled={!message.trim() || connectMutation.isPending}
            data-testid="button-send-connect"
          >
            {connectMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Send request
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RealtorTableRow({ realtor, rowIndex }: { realtor: PublicRealtor; rowIndex: number }) {
  const [showConnect, setShowConnect] = useState(false);

  const details = realtor.details || {};
  const colivingCertified = Boolean(details.colivingCertified);
  const certificationDate = details.certificationDate ? String(details.certificationDate) : null;
  const licenseState = details.licenseState ? String(details.licenseState) : null;
  const colivingDealsCount =
    typeof details.colivingDealsCount === "number" && details.colivingDealsCount >= 0
      ? details.colivingDealsCount
      : null;

  return (
    <>
      <tr
        className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
        data-testid={`row-realtor-${realtor.id}`}
      >
        <td className="py-3 px-4">
          <RealtorAvatar realtor={realtor} size="sm" />
        </td>
        <td className="py-3 px-4">
          <div>
            <span className="font-medium text-gray-900 text-sm" data-testid={`text-realtor-name-${realtor.id}`}>
              {realtor.contactName}
            </span>
            {realtor.companyName && (
              <p className="text-xs text-gray-500" data-testid={`text-realtor-company-${realtor.id}`}>
                {realtor.companyName}
              </p>
            )}
          </div>
        </td>
        <td className="py-3 px-4 hidden sm:table-cell">
          {licenseState ? (
            <span className="text-sm text-gray-700" data-testid={`text-realtor-state-${realtor.id}`}>
              {licenseState}
            </span>
          ) : (
            <span className="text-sm text-gray-400">—</span>
          )}
        </td>
        <td className="py-3 px-4 hidden md:table-cell">
          {colivingCertified ? (
            <div className="flex flex-col gap-0.5">
              <Badge className="bg-green-100 text-green-800 border-green-200 flex items-center gap-1 text-xs w-fit" data-testid={`badge-certified-${realtor.id}`}>
                <Award className="h-3 w-3" />
                Certified
              </Badge>
              {certificationDate && (
                <span className="text-xs text-green-600">
                  <Star className="h-2.5 w-2.5 inline mr-0.5" />
                  {format(new Date(certificationDate), "MMM yyyy")}
                </span>
              )}
            </div>
          ) : (
            <span className="text-sm text-gray-400">—</span>
          )}
        </td>
        <td className="py-3 px-4 hidden sm:table-cell text-center">
          {colivingDealsCount !== null ? (
            <div className="flex items-center gap-1 text-sm text-gray-700" data-testid={`text-deals-count-${realtor.id}`}>
              <Handshake className="h-3.5 w-3.5 text-gray-400 shrink-0" />
              <span>{colivingDealsCount}</span>
            </div>
          ) : (
            <span className="text-sm text-gray-400">—</span>
          )}
        </td>
        <td className="py-3 px-4 text-right">
          <Button
            size="sm"
            onClick={() => setShowConnect(true)}
            data-testid={`button-connect-${realtor.id}`}
            data-tour={rowIndex === 0 ? "find-realtor-connect" : undefined}
            className="whitespace-nowrap"
          >
            <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
            <span className="hidden sm:inline">Work with</span>
            <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </td>
      </tr>
      <ConnectDialog realtor={realtor} open={showConnect} onClose={() => setShowConnect(false)} />
    </>
  );
}

function RealtorTableRowSkeleton() {
  return (
    <tr className="border-b border-gray-100">
      <td className="py-3 px-4"><Skeleton className="w-8 h-8 rounded-full" /></td>
      <td className="py-3 px-4">
        <Skeleton className="h-4 w-32 mb-1" />
        <Skeleton className="h-3 w-20" />
      </td>
      <td className="py-3 px-4 hidden sm:table-cell"><Skeleton className="h-4 w-16" /></td>
      <td className="py-3 px-4 hidden md:table-cell"><Skeleton className="h-5 w-20" /></td>
      <td className="py-3 px-4 hidden sm:table-cell"><Skeleton className="h-4 w-8" /></td>
      <td className="py-3 px-4"><Skeleton className="h-8 w-24 ml-auto" /></td>
    </tr>
  );
}

export default function AppFindRealtor() {
  usePageTitle("Find a Realtor");
  const [selectedState, setSelectedState] = useState<string>("all");

  const { data: realtors, isLoading } = useQuery<PublicRealtor[]>({
    queryKey: ["/api/app/realtors"],
  });

  const certifiedCount = realtors?.filter(r => Boolean(r.details?.colivingCertified)).length ?? 0;

  const stateCounts = useMemo(() => {
    const counts = new Map<string, number>();
    if (!realtors) return counts;
    for (const r of realtors) {
      const s = r.details?.licenseState;
      if (typeof s === "string" && s.trim()) {
        const key = s.trim();
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    }
    return counts;
  }, [realtors]);

  const availableStates = useMemo(() => Array.from(stateCounts.keys()).sort(), [stateCounts]);

  const filteredRealtors = useMemo(() => {
    if (!realtors) return [];
    if (selectedState === "all") return realtors;
    return realtors.filter(r => {
      const s = r.details?.licenseState;
      return typeof s === "string" && s.trim() === selectedState;
    });
  }, [realtors, selectedState]);

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                <Users className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900" data-testid="text-find-realtor-heading" data-tour="find-realtor-heading">Find a Realtor</h1>
                <p className="text-sm text-gray-500">Co-living specialists who understand PadSplit investing</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="find-realtor" />
              <GuideButton pageKey="find-realtor" />
            </div>
          </div>
        </div>

        <GuideNudgeBanner pageKey="find-realtor" />

        {!isLoading && realtors && certifiedCount > 0 && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-100 rounded-xl p-5 mb-6 flex items-start gap-3">
            <Award className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-green-900 text-sm">Co-Living Certified Realtors</p>
              <p className="text-sm text-green-700 mt-0.5">
                {certifiedCount} realtor{certifiedCount !== 1 ? "s" : ""} on PadPulse hold the Co-Living Certified badge, indicating specialized knowledge in PadSplit and co-living investment properties. Certified realtors appear first.
              </p>
            </div>
          </div>
        )}

        {!isLoading && availableStates.length > 0 && (
          <div className="flex items-center gap-3 mb-5" data-tour="find-realtor-filter">
            <span className="text-sm text-gray-600 shrink-0">Filter by state:</span>
            <Select value={selectedState} onValueChange={setSelectedState}>
              <SelectTrigger className="w-48 h-9 text-sm" data-testid="select-state-filter">
                <SelectValue placeholder="All states" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All states ({realtors?.length ?? 0})</SelectItem>
                {availableStates.map(state => (
                  <SelectItem key={state} value={state}>{state} ({stateCounts.get(state) ?? 0})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedState !== "all" && (
              <Button variant="ghost" size="sm" className="h-9 text-xs text-gray-500" onClick={() => setSelectedState("all")} data-testid="button-clear-state-filter">
                Clear
              </Button>
            )}
          </div>
        )}

        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white" data-tour="find-realtor-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="py-3 px-4 text-left font-medium text-gray-500 w-12"></th>
                  <th className="py-3 px-4 text-left font-medium text-gray-500">Name</th>
                  <th className="py-3 px-4 text-left font-medium text-gray-500 hidden sm:table-cell">State</th>
                  <th className="py-3 px-4 text-left font-medium text-gray-500 hidden md:table-cell">Certified</th>
                  <th className="py-3 px-4 text-left font-medium text-gray-500 hidden sm:table-cell">
                    <div className="flex items-center gap-1">
                      <Handshake className="h-3.5 w-3.5" />
                      Closed Deals
                    </div>
                  </th>
                  <th className="py-3 px-4 text-right font-medium text-gray-500"></th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 6 }).map((_, i) => <RealtorTableRowSkeleton key={i} />)
                ) : filteredRealtors.length > 0 ? (
                  filteredRealtors.map((r, i) => <RealtorTableRow key={r.id} realtor={r} rowIndex={i} />)
                ) : (
                  <tr>
                    <td colSpan={6} className="py-16 text-center text-gray-400">
                      <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      {selectedState !== "all" ? (
                        <>
                          <p className="text-lg font-medium">No realtors in {selectedState}</p>
                          <p className="text-sm">Try a different state or <button className="underline" onClick={() => setSelectedState("all")}>view all</button>.</p>
                        </>
                      ) : (
                        <>
                          <p className="text-lg font-medium">No realtors available yet</p>
                          <p className="text-sm">Check back soon — we're onboarding partner realtors now.</p>
                        </>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
